import React, { useEffect, useState, useRef } from 'react';
import { CheckCircle2Icon, XIcon } from 'lucide-react';
// Realistic US male first names + last initials, paired with diverse US cities.
// Distinct entries so the same notification never repeats in a session.
const NOTIFICATIONS = [{
  name: 'Michael R.',
  city: 'Austin, TX'
}, {
  name: 'David K.',
  city: 'Denver, CO'
}, {
  name: 'James P.',
  city: 'Seattle, WA'
}, {
  name: 'Christopher L.',
  city: 'Boston, MA'
}, {
  name: 'Ryan M.',
  city: 'Portland, OR'
}, {
  name: 'Andrew S.',
  city: 'Nashville, TN'
}, {
  name: 'Brandon W.',
  city: 'Charlotte, NC'
}, {
  name: 'Tyler H.',
  city: 'Phoenix, AZ'
}, {
  name: 'Nathan B.',
  city: 'Minneapolis, MN'
}, {
  name: 'Jason F.',
  city: 'Atlanta, GA'
}, {
  name: 'Eric D.',
  city: 'San Diego, CA'
}, {
  name: 'Matthew G.',
  city: 'Dallas, TX'
}, {
  name: 'Kevin O.',
  city: 'Chicago, IL'
}, {
  name: 'Justin V.',
  city: 'Tampa, FL'
}, {
  name: 'Daniel C.',
  city: 'Salt Lake City, UT'
}, {
  name: 'Aaron J.',
  city: 'Raleigh, NC'
}, {
  name: 'Steven T.',
  city: 'Columbus, OH'
}, {
  name: 'Patrick A.',
  city: 'Pittsburgh, PA'
}, {
  name: 'Jordan N.',
  city: 'Indianapolis, IN'
}, {
  name: 'Anthony Y.',
  city: 'Kansas City, MO'
}];
const ACTIONS = ['just got UNHOOKED', 'started reading', 'just purchased', 'unlocked the ebook'];
const TIME_AGOS = ['just now', '1 minute ago', '2 minutes ago', '3 minutes ago', '4 minutes ago', '6 minutes ago', '8 minutes ago'];
type Notice = {
  id: number;
  name: string;
  city: string;
  action: string;
  ago: string;
};
// Timing config — tuned to feel organic, not pushy
const FIRST_DELAY_MS = 12000; // 12s — first notice after the user reads the hero
const FIRST_DELAY_JITTER = 3000; // ±3s randomization
const GAP_MIN_MS = 28000; // 28s minimum between notifications
const GAP_MAX_MS = 50000; // 50s maximum
const VISIBLE_MS = 6000; // 6s on screen
const MAX_NOTIFICATIONS = 6; // hard ceiling per session
const DISMISS_DISABLE_KEY = 'unhooked_notif_dismissed';
function pickRandom<T>(arr: T[], excludeIndex?: number): {
  item: T;
  index: number;
} {
  let idx = Math.floor(Math.random() * arr.length);
  if (excludeIndex !== undefined && arr.length > 1) {
    while (idx === excludeIndex) {
      idx = Math.floor(Math.random() * arr.length);
    }
  }
  return {
    item: arr[idx],
    index: idx
  };
}
function buildNotice(usedIds: Set<number>): Notice | null {
  // Find an unused person
  const available = NOTIFICATIONS.map((_, i) => i).filter(i => !usedIds.has(i));
  if (available.length === 0) return null;
  const personIdx = available[Math.floor(Math.random() * available.length)];
  const person = NOTIFICATIONS[personIdx];
  return {
    id: personIdx,
    name: person.name,
    city: person.city,
    action: pickRandom(ACTIONS).item,
    ago: pickRandom(TIME_AGOS).item
  };
}
export function PurchaseNotifications() {
  const [current, setCurrent] = useState<Notice | null>(null);
  const usedIdsRef = useRef<Set<number>>(new Set());
  const shownCountRef = useRef(0);
  const timersRef = useRef<{
    schedule?: any;
    hide?: any;
  }>({});
  const dismissedRef = useRef(false);
  useEffect(() => {
    // Respect prior dismissal in same session
    try {
      if (sessionStorage.getItem(DISMISS_DISABLE_KEY) === '1') {
        dismissedRef.current = true;
        return;
      }
    } catch {}
    const scheduleNext = (delayMs: number) => {
      timersRef.current.schedule = setTimeout(() => {
        if (dismissedRef.current) return;
        if (shownCountRef.current >= MAX_NOTIFICATIONS) return;
        const notice = buildNotice(usedIdsRef.current);
        if (!notice) return;
        usedIdsRef.current.add(notice.id);
        shownCountRef.current += 1;
        setCurrent(notice);
        // Hide after VISIBLE_MS, then schedule next
        timersRef.current.hide = setTimeout(() => {
          setCurrent(null);
          if (!dismissedRef.current && shownCountRef.current < MAX_NOTIFICATIONS) {
            const gap = GAP_MIN_MS + Math.random() * (GAP_MAX_MS - GAP_MIN_MS);
            scheduleNext(gap);
          }
        }, VISIBLE_MS);
      }, delayMs);
    };
    // First notice with jitter
    const firstDelay = FIRST_DELAY_MS + (Math.random() - 0.5) * 2 * FIRST_DELAY_JITTER;
    scheduleNext(Math.max(8000, firstDelay));
    return () => {
      if (timersRef.current.schedule) clearTimeout(timersRef.current.schedule);
      if (timersRef.current.hide) clearTimeout(timersRef.current.hide);
    };
  }, []);
  const handleDismiss = () => {
    setCurrent(null);
    dismissedRef.current = true;
    try {
      sessionStorage.setItem(DISMISS_DISABLE_KEY, '1');
    } catch {}
    if (timersRef.current.schedule) clearTimeout(timersRef.current.schedule);
    if (timersRef.current.hide) clearTimeout(timersRef.current.hide);
  };
  if (!current) return null;
  return <div role="status" aria-live="polite" className="fixed z-[60] bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-auto sm:max-w-[340px] pointer-events-none" data-id="element-170">
      <div key={current.id} className="pointer-events-auto relative flex items-start gap-3 bg-charcoal-light/95 backdrop-blur-md border border-gold/25 rounded-xl px-4 py-3.5 shadow-2xl shadow-black/40 mp-notif-enter" data-id="element-171">
        {/* Subtle gold accent bar on the left */}
        <div className="absolute left-0 top-3 bottom-3 w-[2px] bg-gold/70 rounded-full" data-id="element-172" />

        {/* Verified check icon */}
        <div className="shrink-0 w-9 h-9 rounded-full bg-teal-dark/40 border border-teal/40 flex items-center justify-center" data-id="element-173">
          <CheckCircle2Icon className="w-5 h-5 text-gold" data-id="element-174" />
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0 pr-5" data-id="element-175">
          <div className="text-ivory text-[13px] sm:text-sm leading-snug" data-id="element-176">
            <span className="font-medium" data-id="element-177">{current.name}</span>
            <span className="text-ivory/60" data-id="element-178"> from </span>
            <span className="text-ivory/85" data-id="element-179">{current.city}</span>
          </div>
          <div className="text-ivory/55 text-[11px] sm:text-xs mt-1 flex items-center gap-1.5" data-id="element-180">
            <span data-id="element-181">{current.action}</span>
            <span className="text-ivory/25" data-id="element-182">·</span>
            <span className="text-gold/70 italic" data-id="element-183">{current.ago}</span>
          </div>
        </div>

        {/* Dismiss */}
        <button onClick={handleDismiss} aria-label="Dismiss notification" className="absolute top-2 right-2 text-ivory/35 hover:text-ivory/80 transition-colors p-1" data-id="element-184">
          <XIcon className="w-3.5 h-3.5" data-id="element-185" />
        </button>
      </div>
    </div>;
}